import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { UIInput, UIReactSelect, UIMultiSelectTeammber } from "components/Inputfields";
import { showError, showSuccess } from "toast/toast";
import Personal_Info from "assets/images/Agents/Personal_Info.png"
import shield from "assets/images/Agents/shield.png"
import Profile from "assets/images/Agents/Profile.png"
import Permission_Assignment from "assets/images/Agents/Permission_Assignment.png"
import { IconHeading } from "components/Iconsborders";
import Select, { SingleValue, components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { Role, RoleAssignment } from "types"
import {
    firstNameValidation,
    lastNameValidation,
    emailValidation,
    phoneValidation,
    // passwordValidation,
} from "validationSchemas/common";
import { getCloudFileById, fileToBase64 } from "components/Imageshelper";
import { getAuthCookie } from "../../utils/authCookies";
type Office = {
    _id: string;
    officeName: string;
    officeType: string;
    officeAddress?: Array<{
        _id: string;
        label?: string;
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        country?: string;
        zip?: string;
        phone?: string;
    }>;
};

type TeamMemberAPI = {
    _id?: string;
    id?: string;
    membership_id?: string;
    global_user_id?: string;
    firstName: string;
    lastName: string;
    jobTitle: string;
    jobTitleId?: string;
    job_title_id?: string;
    /** Free-text role when `jobTitle` is the preset "Custom" (persisted by API when supported). */
    customRole?: string;
    phone1: string;
    phone2?: string;
    email: string;
    accessLevel?: string[];
    status: string;
    profilePic?: string | null;
    officeIDs?: string[];
    offices?: Office[];
    addresses?: {
        personal?: {
            address_details?: Partial<{
                addressLine1: string;
                addressLine2: string;
                city: string;
                state: string;
                country: string;
                zip: string;
                cityCode: string;
                stateCode: string;
                countryCode: string;
                latitude: number;
                longitude: number;
                phone: string;
            }>;
        };
    };
};

const normalizeTeamMemberStatus = (status?: string) => {
    const upper = (status || "").toUpperCase();
    if (upper === "ACTIVE") return "Active";
    if (upper === "INACTIVE") return "Inactive";
    return status || "Active";
};

const getTeamMemberOfficeIds = (member: TeamMemberAPI | null): string[] => {
    if (!member) return [];
    if (Array.isArray(member.officeIDs) && member.officeIDs.length > 0) {
        return member.officeIDs.map(String).filter(Boolean);
    }
    if (Array.isArray(member.offices) && member.offices.length > 0) {
        return member.offices.map((o) => String(o._id || (o as { id?: string }).id || "")).filter(Boolean);
    }
    return [];
};

const getMembershipId = (member: TeamMemberAPI | null, fallback = ""): string =>
    String(member?.membership_id || member?._id || member?.id || fallback).trim();

type Option = { value: string; label: string };
type JobTitleOption = { value: string; label: string; id?: string };

const JOB_TITLE_CUSTOM = "Custom";
const noAutoSuggestFieldProps = {
    autocomplete: "off",
    spellCheck: false,
    autoCorrect: "off",
    autoCapitalize: "off",
    ignorePasswordManagers: true,
};

const STATUS_OPTIONS: Option[] = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];

const PERMISSION_LEVELS: Option[] = [
    { value: "all", label: "All" },
    { value: "view", label: "View" },
    { value: "edit", label: "Edit" },
    { value: "delete", label: "Delete" },
];

// Accordion layout from your mock
const PERM_GROUPS: { key: string; title: string; rows: string[] }[] = [
    { key: "agents", title: "Agents", rows: ["Agents"] },
    {
        key: "inventory",
        title: "Inventory",
        rows: ["Office Inventory", "Agent Inventory", "Office and Agent Inventory", "Brands and Categories"],
    },
    { key: "orders", title: "Orders", rows: ["Orders"] },
    { key: "jobs", title: "Jobs", rows: ["Installations", "Removals"] },
    { key: "suppliers", title: "Suppliers", rows: ["Suppliers"] }, // special columns: Link/Unlink/All (UI only)
];

// 🔧 Keys and labels
type CrudKey = "create" | "view" | "edit" | "delete" | "all";
type SupplierKey = "link" | "unlink" | "all";

const CRUD_KEYS = ["create", "view", "edit", "delete", "all"] as const;
const SUPPLIER_KEYS = ["link", "unlink", "all"] as const;

const CRUD_LABELS: Record<CrudKey, string> = {
    create: "Create", view: "View", edit: "Edit", delete: "Delete", all: "All",
};
const SUPPLIER_LABELS: Record<SupplierKey, string> = {
    link: "Link", unlink: "Unlink", all: "All",
};

// 🔧 Make row values optional booleans for CRUD keys
type PermStateRow = Partial<Record<CrudKey, boolean>>;
type PermState = Record<string, PermStateRow>;

const cellCenter: React.CSSProperties = { textAlign: "center", verticalAlign: "middle" };

/** Hide arrow when disabled */
const DropdownIndicator = (props: any) => {
    if (props.selectProps.isDisabled) return null;
    return (
        <components.DropdownIndicator {...props}>
            <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
        </components.DropdownIndicator>
    );
};

const customSelectStyles = {
    /* hide all indicators when disabled */
    indicatorsContainer: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    dropdownIndicator: (provided: any, state: any) => ({
        ...provided,
        display: state.isDisabled ? "none" : "flex",
    }),
    indicatorSeparator: () => ({ display: "none" }),

    /* keep bg and cursor right; do NOT fade */
    control: (provided: any, state: any) => ({
        ...provided,
        backgroundColor: state.isDisabled ? "#f2f2f2" : provided.backgroundColor,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        opacity: 1,
    }),

    /* ——— make text BLACK when disabled ——— */
    singleValue: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    valueContainer: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
    }),
    input: (provided: any, state: any) => ({
        ...provided,
        color: state.isDisabled ? "#000" : provided.color,
        WebkitTextFillColor: state.isDisabled ? "#000" : undefined, // Safari
    }),
    placeholder: (provided: any, state: any) => ({
        ...provided,
        /* if field has no value, keep placeholder readable too */
        color: state.isDisabled ? "#000" : provided.color,
    }),

    /* if you ever use isMulti, keep chips readable */
    multiValue: (p: any, s: any) => ({
        ...p,
        backgroundColor: s.isDisabled ? "#e5e7eb" : p.backgroundColor,
    }),
    multiValueLabel: (p: any, s: any) => ({
        ...p,
        color: s.isDisabled ? "#000" : p.color,
    }),
    multiValueRemove: (p: any, s: any) => ({
        ...p,
        display: s.isDisabled ? "none" : p.display,
    }),
};
const toUrlString = (res: unknown): string | null => {
  if (!res) return null;
  if (typeof res === "string") return res;
  const r = res as any;
  const u = r?.url ?? r?.signedUrl ?? r?.data?.url;
  return typeof u === "string" ? u : null;
};

export default function SignagebrokerageAddEditTeammember() {
    const { firmId } = useParams<{ firmId: string }>();
    const { teammemberId } = useParams<{ teammemberId: string }>();
    const isEdit = Boolean(teammemberId);
    const navigate = useNavigate();
    const token = getAuthCookie("sessionToken") || "";
    const sessionLicenseType = String(getAuthCookie("licenseType") || "").trim().toUpperCase();
    const allowExistingMemberAccessConfirm =
        sessionLicenseType === "SIGNAGE_FIRM" || sessionLicenseType === "SIGNAGE_TEAM_MEMBER";

    const [loading, setLoading] = useState<boolean>(isEdit);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [memberAccessPrompt, setMemberAccessPrompt] = useState<{
        message: string;
        name: string;
        email: string;
    } | null>(null);
    const [memberAccessConfirmed, setMemberAccessConfirmed] = useState(false);
    const confirmMemberAccessRef = useRef(false);

    const [offices, setOffices] = useState<Office[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [assignedRoles, setAssignedRoles] = useState<RoleAssignment[]>([]);
    const [roleAssignmentId, setRoleAssignmentId] = useState<string>(""); // Store the role assignment ID for updates
    const [member, setMember] = useState<TeamMemberAPI | null>(null);
     const [cloudFileId, setCloudFileId] = useState<string | null>(null);
     const [preview, setPreview] = useState<string | null>(null);
     const [serverImageSrc, setServerImageSrc] = useState<string | null>(null);
   
   
     const is24Hex = (s?: string | null) => !!s && /^[0-9a-f]{24}$/i.test(s);
     const normalizePath = (p: string) => p.replace(/\\/g, "/").replace(/^\/+/, "");
     const buildAbsoluteFromRelative = (p: string) => {
       const base = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
       return `${base}/${normalizePath(p)}`;
     };
     useEffect(() => {
       let active = true;
       (async () => {
         const val = member?.profilePic;
         if (!val) {
           if (active) setServerImageSrc(null);
           return;
         }
         if (/^https?:\/\//i.test(val)) {
           if (active) setServerImageSrc(val);
           return;
         }
         if (is24Hex(val)) {
           setCloudFileId(val);
           try {
             const res = await getCloudFileById(val, token as string);
             const u = toUrlString(res);
             if (active) setServerImageSrc(u);
           } catch {
             if (active) setServerImageSrc(null);
           }
           return;
         }
         if (active) setServerImageSrc(buildAbsoluteFromRelative(val));
       })();
       return () => {
         active = false;
       };
     }, [member?.profilePic, token]);

    const loadJobTitleOptions: LoadOptions<JobTitleOption, any, { page: number }> = useCallback(
        async (inputValue, _loadedOptions, additional) => {
            const page = additional?.page ?? 1;

            if (!token) {
                return {
                    options: [{ value: JOB_TITLE_CUSTOM, label: "Custom", id: JOB_TITLE_CUSTOM }],
                    hasMore: false,
                    additional: { page: 1 },
                };
            }

            try {
                const query = new URLSearchParams({
                    page: String(page),
                    limit: "10",
                });

                if (String(inputValue || "").trim()) {
                    query.set("search", String(inputValue).trim());
                }

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/job-titles/select2?${query.toString()}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                const result = await response.json();
                if (!response.ok) {
                    throw new Error(result?.message || result?.error || "Failed to load job titles");
                }

                const apiOptions: JobTitleOption[] = Array.isArray(result?.results)
                    ? result.results
                        .filter((item: any) => item?.status !== false)
                        .map((item: any) => ({
                            value: String(item?.title || item?.text || item?.id || item?._id || ""),
                            label: String(item?.text || item?.title || ""),
                            id: String(item?.id || item?._id || ""),
                        }))
                        .filter((item: JobTitleOption) => item.value && item.label)
                    : [];

                const customOption: JobTitleOption = {
                    value: JOB_TITLE_CUSTOM,
                    label: "Custom",
                    id: JOB_TITLE_CUSTOM,
                };

                const options = [...apiOptions, customOption];

                return {
                    options,
                    hasMore: page * 10 < Number(result?.total || 0),
                    additional: { page: page + 1 },
                };
            } catch (error) {
                console.warn("Failed to load job title options:", error);
                return {
                    options: [{ value: JOB_TITLE_CUSTOM, label: "Custom", id: JOB_TITLE_CUSTOM }],
                    hasMore: false,
                    additional: { page: page + 1 },
                };
            }
        },
        [token]
    );
    // Permissions matrix state for accordion (UI only; we still send accessLevel array)
    const [permState, setPermState] = useState<PermState>({});
    const [showPassword, setShowPassword] = useState(false);

    // seed permission rows
    useEffect(() => {
        const initial: PermState = {};
        PERM_GROUPS.forEach(g =>
            g.rows.forEach(r => (initial[r] = { create: false, view: false, edit: false, delete: false, all: false }))
        );
        setPermState(initial);
    }, []);

    // fetch offices
    useEffect(() => {
        const brokerageId = String(firmId || "").trim();
        if (!brokerageId) {
            setOffices([]);
            return;
        }

        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/organizations/${brokerageId}/offices`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                const data = await res.json();
                if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch offices");
                setOffices(Array.isArray(data) ? (data as Office[]) : []);
            } catch (e: any) {
                showError(e.message || "Failed to fetch offices");
                setOffices([]);
            }
        })();
    }, [token, firmId]);

    // fetch roles
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/roles`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch roles");
                setRoles(Array.isArray(data) ? (data as Role[]) : []);
            } catch (e: any) {
                showError(e.message || "Failed to fetch roles");
            }
        })();
    }, [token]);



    // fetch member (edit)
    useEffect(() => {
        if (!isEdit) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/organization-memberships/${teammemberId}`,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                const data = (await res.json()) as TeamMemberAPI;
                if (!res.ok) {
                    throw new Error(
                        (data as { error?: string; message?: string })?.error ||
                            (data as { error?: string; message?: string })?.message ||
                            "Failed to load team member"
                    );
                }
                setMember(data);

                // hydrate perm matrix from accessLevel
                const level = data.accessLevel || [];
                setPermState(prev => {
                    const next: PermState = { ...prev };
                    const allOn = level.includes("all");
                    Object.keys(next).forEach(k => {
                        next[k] = allOn
                            ? { create: true, view: true, edit: true, delete: true, all: true }
                            : {
                                create: false,
                                view: level.includes("view"),
                                edit: level.includes("edit"),
                                delete: level.includes("delete"),
                                all: false,
                            };
                    });
                    return next;
                });
            } catch (e: any) {
                showError(e.message || "Failed to load team member");
            } finally {
                setLoading(false);
            }
        })();
    }, [isEdit, teammemberId, token]);

    // fetch assigned roles (edit mode)
    useEffect(() => {
        if (!isEdit || !member) return;

        const roleUserId = getMembershipId(member, teammemberId || "");
        if (!roleUserId) return;

        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/user-role-assignments/all-roles/${roleUserId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                if (!res.ok || data?.error) throw new Error(data?.error || "Failed to fetch assigned roles");
                const roleAssignments = Array.isArray(data) ? (data as RoleAssignment[]) : [];
                setAssignedRoles(roleAssignments);
                // Store the first assignment ID for updates
                if (roleAssignments.length > 0) {
                    setRoleAssignmentId(roleAssignments[0]._id);
                }
            } catch (e: any) {
                console.warn("Failed to fetch assigned roles:", e.message);
                setAssignedRoles([]);
            }
        })();
    }, [isEdit, member, teammemberId, token]);

    const officeOptions: Option[] = useMemo(
        () => (offices ?? []).map(o => ({ value: o._id, label: o.officeName })),
        [offices]
    );

    const roleOptions: Option[] = useMemo(
        () => (roles ?? []).map(r => ({ value: r._id, label: r.name })),
        [roles]
    );
  
    const DEFAULT_TEN_ZEROS = "0000000000";
    const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
        !v || v.trim() === "" || v === def ? def : v;
    const toCoordinate = (value: unknown) => {
        const coordinate = Number(value ?? 0);
        return Number.isFinite(coordinate) ? coordinate : 0;
    };
    const US_COUNTRY_DEFAULTS = { country: "United States", countryCode: "US" } as const;


    const initialValues = useMemo(
        () => ({
            // personal
            firstName: member?.firstName || "",
            lastName: member?.lastName || "",
            phone: member?.phone1 || DEFAULT_TEN_ZEROS,
            secondaryPhone: member?.phone2 || "",
            // account (hidden while editing)
            email: member?.email || "",
            password: "",
            // profile/assignment
            jobTitle: member?.jobTitle || "",
            jobTitleId: member?.jobTitleId || member?.job_title_id || "",
            customRole: String(member?.customRole ?? (member as any)?.custom_role ?? "").trim(),
            assignedOffices: getTeamMemberOfficeIds(member),
            brokeragePermissions: assignedRoles.length > 0 ? assignedRoles[0].role_id : "", // Single role ID
            statuss: normalizeTeamMemberStatus(member?.status),
            image: null as File | null,
            profilePic: member?.profilePic || null,
        }),
        [member, assignedRoles]
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object({
            firstName: firstNameValidation,
            lastName: lastNameValidation,
            phone: phoneValidation,
            email: isEdit ? Yup.string() : emailValidation,
            // password: isEdit ? Yup.string() : passwordValidation,
            jobTitle: Yup.string().required("Role is required"),
            jobTitleId: Yup.string().notRequired(),
            customRole: Yup.string().when("jobTitle", {
                is: JOB_TITLE_CUSTOM,
                then: (schema) =>
                    schema.trim().required("Custom role is required").min(1, "Enter the custom role"),
                otherwise: (schema) => schema.notRequired(),
            }),
            // assignedOffices: assignedOfficesValidation,
            // brokeragePermissions: Yup.string().required("Role & Permissions is required"),
            // statuss: statusValidation,
        }),
        onSubmit: async (values) => {
            try {
                setFormSubmitting(true);

                const profilePicPayload =
                    values.image instanceof File ? await fileToBase64(values.image) : undefined;
                const customJobTitle = String(values.customRole ?? "").trim();
                let resolvedJobTitle =
                    values.jobTitle === JOB_TITLE_CUSTOM && customJobTitle
                        ? customJobTitle
                        : values.jobTitle;
                let resolvedJobTitleId = String((values as any).jobTitleId || "").trim();

                if (values.jobTitle === JOB_TITLE_CUSTOM && customJobTitle) {
                    const createJobTitleResponse = await fetch(`${process.env.REACT_APP_API_URL}/job-titles`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            title: customJobTitle,
                            description: "",
                            status: true,
                        }),
                    });

                    const createJobTitleResult = await createJobTitleResponse.json();
                    if (!createJobTitleResponse.ok || createJobTitleResult?.error) {
                        throw new Error(createJobTitleResult?.message || createJobTitleResult?.error || "Failed to create job title");
                    }

                    resolvedJobTitle = String(createJobTitleResult?.title || customJobTitle);
                    resolvedJobTitleId = String(createJobTitleResult?.id || createJobTitleResult?._id || "").trim();
                }

                const scopeFirmId = String(firmId || "").trim();
                const phone2 = String(values.secondaryPhone || "").trim();
                const nextEmail = String(values.email || "").trim();
                const originalEmail = String(member?.email || "").trim();
                const emailChanged =
                    nextEmail.toLowerCase() !== originalEmail.toLowerCase();
                const nextPassword = String(values.password || "").trim();

                const firstOffice = (offices || []).find((o) =>
                    values.assignedOffices.includes(o._id)
                );
                const officeAddr = firstOffice?.officeAddress?.[0];
                const memberAddr = member?.addresses?.personal?.address_details;
                const addrSource = (memberAddr || officeAddr || {}) as Record<string, unknown>;

                const payload: Record<string, unknown> = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone1: withDefault(values.phone) || values.phone,
                    phone2,
                    jobTitle: resolvedJobTitle,
                    ...(resolvedJobTitleId ? { jobTitleId: resolvedJobTitleId } : {}),
                    ...(profilePicPayload ? { profilePic: profilePicPayload } : {}),
                    status: (values.statuss || "Active").toUpperCase(),
                    userType: "team_member",
                    licenseType: "BROKERAGE_TEAM_MEMBER",
                    officeIDs: values.assignedOffices,
                    address: {
                        addressLine1: String(addrSource?.addressLine1 || ""),
                        addressLine2: String(addrSource?.addressLine2 || ""),
                        city: String(addrSource?.city || ""),
                        state: String(addrSource?.state || ""),
                        country: String(addrSource?.country || US_COUNTRY_DEFAULTS.country),
                        zip: String(addrSource?.zip || ""),
                        phone: withDefault(values.phone),
                        latitude: toCoordinate(addrSource?.latitude),
                        longitude: toCoordinate(addrSource?.longitude),
                        cityCode: String(addrSource?.cityCode || addrSource?.city || ""),
                        stateCode: String(addrSource?.stateCode || ""),
                        countryCode: String(addrSource?.countryCode || US_COUNTRY_DEFAULTS.countryCode),
                    },
                };

                if (!isEdit) {
                    payload.email = nextEmail;
                    payload.password = "";
                } else if (emailChanged) {
                    payload.email = nextEmail;
                }
                if (isEdit && nextPassword) {
                    payload.password = nextPassword;
                }
                if (allowExistingMemberAccessConfirm && confirmMemberAccessRef.current) {
                    payload.confirm_existing_brokerage_member_access = true;
                }
                confirmMemberAccessRef.current = false;

                if (!isEdit && !scopeFirmId) {
                    throw new Error("Please select a brokerage firm");
                }

                const teamMemberMembershipId = getMembershipId(member, teammemberId || "");

                if (isEdit) {
                    if (!scopeFirmId) {
                        throw new Error("Brokerage organization ID is missing");
                    }
                    if (!teamMemberMembershipId) {
                        throw new Error("Team member membership ID is missing");
                    }
                }

                const url = isEdit
                    ? `${process.env.REACT_APP_API_URL}/organizations/${scopeFirmId}/memberships/team-members/${teamMemberMembershipId}`
                    : `${process.env.REACT_APP_API_URL}/organizations/${scopeFirmId}/memberships/team-members`;
                const method = isEdit ? "PATCH" : "POST";

                const res = await fetch(url, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
                const json = await res.json();
                if (
                    allowExistingMemberAccessConfirm &&
                    (json?.code === "BROKERAGE_MEMBER_ACCESS_CONFIRMATION_REQUIRED" ||
                        json?.requires_confirmation === true)
                ) {
                    const existing = json?.existing_member || {};
                    setMemberAccessConfirmed(false);
                    setMemberAccessPrompt({
                        message:
                            json?.message ||
                            "A team member with this email already exists in the brokerage. Do you want to ask the brokerage to give your vendor access to this team member?",
                        name: String(existing?.name || "").trim(),
                        email: String(existing?.email || "").trim(),
                    });
                    return;
                }
                if (!res.ok || json?.error) {
                    throw new Error(json?.error || json?.message || "Failed to save");
                }

                // Get membership ID from response (_id / membership_id only)
                const userId =
                    json?.membership_id ||
                    json?._id ||
                    json?.data?.membership_id ||
                    json?.data?._id ||
                    getMembershipId(member, teammemberId || "");

                // Handle role assignment (single role)
                if (values.brokeragePermissions) {
                    if (isEdit) {
                        // Edit mode: Update existing role assignment using PATCH
                        if (roleAssignmentId) {
                            try {
                                const updateRes = await fetch(`${process.env.REACT_APP_API_URL}/user-role-assignments/${roleAssignmentId}`, {
                                    method: "PATCH",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${token}`,
                                    },
                                    body: JSON.stringify({
                                        user_id: userId,
                                        role_id: values.brokeragePermissions,
                                        scope_node_id: firmId,
                                        scope_node_type: "brokerage",
                                        can_delegate: false,
                                        is_default: false
                                    }),
                                });

                                if (!updateRes.ok) {
                                    const errorData = await updateRes.json();
                                    console.warn(`Failed to update role assignment ${roleAssignmentId}:`, errorData?.error || "Unknown error");
                                }
                            } catch (error) {
                                console.warn(`Error updating role assignment ${roleAssignmentId}:`, error);
                            }
                        }
                        else {
                            try {
                                const roleAssignmentRes = await fetch(`${process.env.REACT_APP_API_URL}/user-role-assignments`, {
                                    method: "POST",
                                    headers: {
                                        "Content-Type": "application/json",
                                        Authorization: `Bearer ${token}`,
                                    },
                                    body: JSON.stringify({
                                        user_id: userId,
                                        role_id: values.brokeragePermissions,
                                        scope_node_id: firmId,
                                        scope_node_type: "brokerage",
                                        can_delegate: false,
                                        is_default: false
                                    }),
                                });

                                if (roleAssignmentRes.ok) {
                                    const assignmentData = await roleAssignmentRes.json();
                                    // Store the assignment ID for future updates
                                    setRoleAssignmentId(assignmentData._id || assignmentData.data?._id);
                                } else {
                                    const errorData = await roleAssignmentRes.json();
                                    console.warn(`Failed to assign role ${values.brokeragePermissions}:`, errorData?.error || "Unknown error");
                                }
                            } catch (roleError) {
                                console.warn(`Error assigning role ${values.brokeragePermissions}:`, roleError);
                            }
                        }
                    } else {
                        // Create mode: Assign role to new user and store the assignment ID
                        try {
                            const roleAssignmentRes = await fetch(`${process.env.REACT_APP_API_URL}/user-role-assignments`, {
                                method: "POST",
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                },
                                body: JSON.stringify({
                                    user_id: userId,
                                    role_id: values.brokeragePermissions,
                                    scope_node_id: firmId,
                                    scope_node_type: "brokerage",
                                    can_delegate: false,
                                    is_default: false
                                }),
                            });

                            if (roleAssignmentRes.ok) {
                                const assignmentData = await roleAssignmentRes.json();
                                // Store the assignment ID for future updates
                                setRoleAssignmentId(assignmentData._id || assignmentData.data?._id);
                            } else {
                                const errorData = await roleAssignmentRes.json();
                                console.warn(`Failed to assign role ${values.brokeragePermissions}:`, errorData?.error || "Unknown error");
                            }
                        } catch (roleError) {
                            console.warn(`Error assigning role ${values.brokeragePermissions}:`, roleError);
                        }
                    }
                }

                showSuccess(isEdit ? "Team member updated successfully!" : "Team member created successfully!");
                navigate(-1);
            } catch (e: any) {
                showError(e.message || "Failed to save team member");
            } finally {
                setFormSubmitting(false);
            }
        },
    });

    const closeMemberAccessPrompt = () => {
        if (formSubmitting) return;
        confirmMemberAccessRef.current = false;
        setMemberAccessConfirmed(false);
        setMemberAccessPrompt(null);
    };

    const confirmExistingMemberAccess = () => {
        if (!memberAccessConfirmed) {
            showError("Select the confirmation to continue.");
            return;
        }
        confirmMemberAccessRef.current = true;
        setMemberAccessPrompt(null);
        formik.handleSubmit();
    };

    useEffect(() => {
        if (formik.values.jobTitle !== JOB_TITLE_CUSTOM && formik.values.customRole) {
            void formik.setFieldValue("customRole", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- clear free text when leaving "Custom" preset
    }, [formik.values.jobTitle]);

    // preview
    useEffect(() => {
        if (!formik.values.image) return setPreview(null);
        const url = URL.createObjectURL(formik.values.image);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [formik.values.image]);

    if (loading) return <div className="content">Loading...</div>;

    // accordion helpers
    // 🔧 only CRUD keys are toggleable in state
    const toggleCell = (row: string, key: CrudKey) =>
        setPermState(prev => {
            const next = { ...prev };
            const curr: PermStateRow = {
                create: false, view: false, edit: false, delete: false, all: false,
                ...next[row],
            };

            // toggle
            curr[key] = !curr[key];

            // simple rules
            if (key === "all" && curr.all) {
                curr.create = curr.view = curr.edit = curr.delete = true;
            }
            // if ((["create","view","edit","delete"] as const).includes(key) && !curr[key]) {
            //   curr.all = false;
            // }

            next[row] = curr;
            return next;
        });
  
          const joinUrl = (base?: string, path?: string) => {
    if (!path) return "";
    if (!base) return path;
    if (/^https?:\/\//i.test(path)) return path;
    const b = base.replace(/\/+$/, "");
    const p = path.replace(/^\/+/, "");
    return `${b}/${p}`;
  };
  const displaySrc =
    preview ??
    serverImageSrc ??
    // last resort: try to build from a relative profilePic
    (typeof formik.values.profilePic === "string" && /^\/?uploads\//i.test(formik.values.profilePic)
      ? joinUrl(process.env.REACT_APP_BASE_URL, formik.values.profilePic)
      : null);

    return (
        <div className="content container">
            <div className="">
                <h3 className="m-0 ">{isEdit ? "Edit Team Member" : "Add New Team Member"}</h3>
                <div className="text-muted mt-1" style={{ fontSize: 14 }}>
                    Create a new team member profile for your real estate team
                </div>
            </div>

            <form
                className="row ms-0 mt-4 mb-5"
                autoComplete="off"
                onSubmit={(e) => {
                    e.preventDefault();
                }}
            >
                <div className="col-md-12" id="row-overlap">
                    <div className="d-none d-md-block">
                        <div className="addheaders-content card-bg-fill shadow-border-1 box-shadow-datatables view-cards-shadow px-5 py-4">
                            <div className="row g-4 mt-3">
                                {/* Personal Information */}
                                <div className="col-lg-6">
                                    <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white">
                                        <div className="d-flex align-items-center mb-5">
                                            <IconHeading label="Personal Information" iconUrl={Personal_Info} color="#F54A00" />
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="firstName" label="First Name" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="lastName" label="Last Name" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="phone" label="Phone" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="secondaryPhone" label="Secondary Phone" placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white" style={{ height: "217px" }}>
                                        <div className="d-flex align-items-center mb-5">
                                            <IconHeading label="Account Access" iconUrl={shield} color="#dcfce7" />

                                        </div>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label" htmlFor="brokerage-teammember-contact-md">
                                                    Email <span className="text-danger">*</span>
                                                </label>
                                                <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }} aria-hidden="true">
                                                    <input type="text" name="username" autoComplete="username" tabIndex={-1} />
                                                    <input type="password" name="password" autoComplete="new-password" tabIndex={-1} />
                                                </div>
                                                <input
                                                    id="brokerage-teammember-contact-md"
                                                    name="brokerageTeammemberContactMd"
                                                    type="text"
                                                    className={`form-control ${(formik.touched as any).email && (formik.errors as any).email ? "is-invalid" : ""}`}
                                                    style={{
                                                        width: "100%",
                                                        height: 39,
                                                        borderRadius: 10,
                                                        padding: "10px 12px",
                                                        borderWidth: 2,
                                                        borderStyle: "solid",
                                                        borderColor: (formik.touched as any).email && (formik.errors as any).email ? "#747981" : "#c9ced6",
                                                        backgroundColor: "#f5f6fa",
                                                        outline: "none",
                                                        boxShadow: "none",
                                                        color: "#111827",
                                                    }}
                                                    value={formik.values.email}
                                                    onChange={(e) => formik.setFieldValue("email", e.target.value)}
                                                    onBlur={() => {
                                                        formik.setFieldValue("email", String(formik.values.email || "").trim());
                                                        formik.setFieldTouched("email", true);
                                                    }}
                                                    placeholder=""
                                                    autoComplete="new-password"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                    data-lpignore="true"
                                                    data-1p-ignore="true"
                                                    data-bwignore="true"
                                                    data-form-type="other"
                                                />
                                                {(formik.touched as any).email && (formik.errors as any).email && (
                                                    <div className="invalid-feedback d-block">{String((formik.errors as any).email)}</div>
                                                )}
                                            </div>
                                            {/* <div className="col-12">
                                                <label className="form-label">
                                                    Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className={`form-control br-8 pe-5 ${formik.touched.password && formik.errors.password ? " is-invalid" : ""}`}
                                                        id="password"
                                                        name="password"
                                                        value={formik.values.password}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        placeholder=""
                                                        disabled={isEdit}
                                                        autoComplete={!isEdit ? "new-password" : undefined}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        style={{ border: "none", background: "none", padding: "8px" }}
                                                    >
                                                        <i className={showPassword ? "ri-eye-off-line fs-17" : "ri-eye-line fs-17"} />
                                                    </button>
                                                </div>
                                                {formik.touched.password && formik.errors.password && (
                                                    <div className="text-danger mt-1">{formik.errors.password as any}</div>
                                                )}
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Profile / Assignment */}
                            <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white mt-4">
                                <div className="d-flex align-items-center mb-5">
                                    <IconHeading label="Profile" iconUrl={Profile} color="#155dfc" />
                                </div>

                                <div className="row g-4 align-items-end">
                                    {/* Upload tile */}
                                    <div className="col-md-2">
                                        <div className="form-label">Profile Image</div>
                                        <label
                                            htmlFor="image"
                                            className="d-inline-flex align-items-center justify-content-center mb-2"
                                            style={{
                                                width: 120,
                                                height: 120,
                                                border: "2px dashed #d0d5dd",
                                                borderRadius: 12,
                                                cursor: "pointer",
                                            }}
                                            title="Upload image"
                                        >
                                           {displaySrc ? (
                            <img
                              src={displaySrc}
                              alt="Preview"
                              style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10 }}
                            />
                          ) : (
                            <i className="bi bi-upload" />
                          )}
                                        </label>
                                        <input
                                            id="image"
                                            name="image"
                                            type="file"
                                            accept="image/*"
                                            className="d-none"
                                            onChange={(e) => {
                                                const f = e.currentTarget.files?.[0] ?? null;
                                                formik.setFieldValue("image", f);
                                            }}
                                            onBlur={formik.handleBlur}
                                        />
                                        <div>
                                            <button
                                                type="button"
                                                className="btn upload-btn mt-2"
                                                onClick={() => document.getElementById("image")?.click()}
                                            >
                                                Upload Image
                                            </button>
                                            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                                                JPG, PNG up to 10MB
                                            </div>
                                        </div>
                                    </div>

                                    {/* Role */}
                                    <div className="col-md-2">
                                        <label className="form-label">
                                            Job Title / Role <span className="text-danger">*</span>
                                        </label>
                                        <AsyncPaginate
                                            classNamePrefix="rs"
                                            placeholder="Select job title/role"
                                            value={
                                                formik.values.jobTitle
                                                    ? {
                                                        value: (formik.values as any).jobTitleId || formik.values.jobTitle,
                                                        label:
                                                            formik.values.jobTitle === JOB_TITLE_CUSTOM
                                                                ? "Custom"
                                                                : formik.values.jobTitle,
                                                    }
                                                    : null
                                            }
                                            loadOptions={loadJobTitleOptions}
                                            onChange={(selected) => {
                                                formik.setFieldValue("jobTitle", selected?.value || "");
                                                formik.setFieldValue("jobTitleId", selected?.id || selected?.value || "");
                                            }}
                                            onBlur={() => formik.setFieldTouched("jobTitle", true)}
                                            additional={{ page: 1 }}
                                            defaultOptions={false}
                                            loadOptionsOnMenuOpen
                                            debounceTimeout={300}
                                            isClearable
                                            components={{ DropdownIndicator }}
                                            styles={customSelectStyles}
                                        />
                                        {formik.touched.jobTitle && formik.errors.jobTitle && (
                                            <div className="text-danger mt-1">{formik.errors.jobTitle as any}</div>
                                        )}
                                    </div>
                                    {formik.values.jobTitle === JOB_TITLE_CUSTOM ? (
                                        <div className="col-md-2">
                                            <UIInput
                                                formik={formik as any}
                                                name="customRole"
                                                label="Custom Role"
                                                required
                                                placeholder="Enter the custom role"
                                                {...noAutoSuggestFieldProps}
                                            />
                                        </div>
                                    ) : null}

                                    {/* Assign Offices (multi) */}
                                    <div className="col-md-3">
                                        <UIMultiSelectTeammber
                                            formik={formik as any}
                                            name="assignedOffices"
                                            label="Assign Offices"
                                            // required
                                            options={officeOptions}
                                            placeholder="Select offices"
                                            isMulti
                                        />
                                    </div>

                                    {/* Role & Permissions */}
                                    {/* <div className="col-md-3 mb-3">
                                        <label className="form-label">
                                            Role & Permissions <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            options={roleOptions}
                                            value={roleOptions.find(o => o.value === formik.values.brokeragePermissions) || null}
                                            onChange={(opt) => formik.setFieldValue("brokeragePermissions", opt?.value || "")}
                                            onBlur={() => formik.setFieldTouched("brokeragePermissions", true)}
                                            placeholder="Choose role/permission"
                                            classNamePrefix="rs"
                                            styles={customSelectStyles}
                                            components={{ DropdownIndicator }}
                                        />
                                        {formik.touched.brokeragePermissions && formik.errors.brokeragePermissions && (
                                            <div className="text-danger mt-1">{formik.errors.brokeragePermissions as any}</div>
                                        )}
                                    </div> */}

                                    {/* Status */}
                                    <div className="col-md-2">
                                        <UIReactSelect
                                            formik={formik as any}
                                            name="statuss"
                                            label="Status"
                                            // required
                                            options={STATUS_OPTIONS}
                                            placeholder="Select status"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* --- local styles (keep in same file) --- */}
                            <style>{`
  /* switcher: desktop table vs mobile cards */
  .perm-desktop { display: none; }
  .perm-mobile  { display: block; }
  @media (min-width: 768px) {
    .perm-desktop { display: block; }
    .perm-mobile  { display: none; }
  }

  /* mobile card grid */
  .perm-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px 10px;
    align-items: center;
  }
  @media (max-width: 380px) {
    .perm-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .perm-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-direction: column;
    text-align: center;
  }
  .perm-checkbox { width: 20px; height: 20px; } /* larger tap target */
`}</style>

                            {/* Permissions & Assignment (accordion) */}
                            {/* <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white mt-4">
                                <div className="d-flex align-items-center mb-5">
                                    <IconHeading label="Permissions & Assignment" iconUrl={Permission_Assignment} color="#7203eb" />
                                </div>

                                
                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <UIMultiSelectTeammber
                                            formik={formik as any}
                                            name="permissions"
                                            label="Permissions Level"
                                            required
                                            options={PERMISSION_LEVELS}
                                            placeholder="Select permissions level"
                                            isMulti
                                            onChangeOverride={(opts: any) => {
                                                const vals: string[] = (opts || []).map((o: any) => o.value);
                                                formik.setFieldValue(
                                                    "permissions",
                                                    vals.includes("all") ? PERMISSION_LEVELS.map(o => o.value) : vals
                                                );
                                            }}
                                        />
                                    </div>
                                </div>


                                <div className="accordion" id="permAccordion">
                                    {PERM_GROUPS.map((g, idx) => (
                                        <div className="accordion-item mb-2 border rounded-3" key={g.key}>
                                            <h2 className="accordion-header">
                                                <button
                                                    className={`accordion-button ${idx !== 0 ? "collapsed" : ""}`}
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target={`#collapse-${g.key}`}
                                                    aria-expanded={idx === 0 ? "true" : "false"}
                                                >
                                                    {g.title}
                                                </button>
                                            </h2>

                                            <div
                                                id={`collapse-${g.key}`}
                                                className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                                                data-bs-parent="#permAccordion"
                                            >
                                                <div className="accordion-body p-0">

                                                    
                                                    <div className="table-responsive perm-desktop">
                                                        <table className="table mb-0 align-middle">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th style={{ width: "36%" }}>Module</th>
                                                                    {g.key === "suppliers" ? (
                                                                        <>
                                                                            <th className="text-center">Link</th>
                                                                            <th className="text-center">Unlink</th>
                                                                            <th className="text-center">All</th>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <th className="text-center">Create</th>
                                                                            <th className="text-center">View</th>
                                                                            <th className="text-center">Edit</th>
                                                                            <th className="text-center">Delete</th>
                                                                            <th className="text-center">All</th>
                                                                        </>
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {g.rows.map((r) => (
                                                                    <tr key={r}>
                                                                        <td className="py-3">{r}</td>

                                                                        {g.key === "suppliers" ? (
                                                                            <>
                                                                                <td className="text-center">
                                                                                    <input type="checkbox" className="form-check-input" />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input type="checkbox" className="form-check-input" />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input type="checkbox" className="form-check-input" />
                                                                                </td>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <td className="text-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!permState[r]?.create}
                                                                                        onChange={() => toggleCell(r, "create")}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!permState[r]?.view}
                                                                                        onChange={() => toggleCell(r, "view")}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!permState[r]?.edit}
                                                                                        onChange={() => toggleCell(r, "edit")}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!permState[r]?.delete}
                                                                                        onChange={() => toggleCell(r, "delete")}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        className="form-check-input"
                                                                                        checked={!!permState[r]?.all}
                                                                                        onChange={() => toggleCell(r, "all")}
                                                                                    />
                                                                                </td>
                                                                            </>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div> */}


                            {/* Footer */}
                            <div className="d-flex justify-content-end align-items-center mt-5">
                                <button type="button" className="btn btn-primary save-btn surface-btn fw-bold fs-16 me-3" disabled={formSubmitting} onClick={() => formik.handleSubmit()}>
                                    {formSubmitting ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create Team Member"}
                                </button>
                                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => navigate(-1)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="d-block d-md-none">
                        <div className="">
                            <div className="row g-4 mt-3">
                                {/* Personal Information */}
                                <div className="col-lg-6">
                                    <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white">
                                        <div className="d-flex align-items-center mb-5">
                                            <i className="bi bi-person-fill text-warning me-2" />
                                            <div className="fw-semibold">Personal Information</div>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="firstName" label="First Name" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="lastName" label="Last Name" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="phone" label="Phone" required placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                            <div className="col-md-6">
                                                <UIInput formik={formik as any} name="secondaryPhone" label="Secondary Phone" placeholder="" {...noAutoSuggestFieldProps} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white" style={{ height: "330px" }}>
                                        <div className="d-flex align-items-center mb-5">
                                            <i className="bi bi-shield-check text-success me-2" />
                                            <div className="fw-semibold">Account Access</div>
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label" htmlFor="brokerage-teammember-contact-xs">
                                                    Email <span className="text-danger">*</span>
                                                </label>
                                                <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", opacity: 0 }} aria-hidden="true">
                                                    <input type="text" name="username" autoComplete="username" tabIndex={-1} />
                                                    <input type="password" name="password" autoComplete="new-password" tabIndex={-1} />
                                                </div>
                                                <input
                                                    id="brokerage-teammember-contact-xs"
                                                    name="brokerageTeammemberContactXs"
                                                    type="text"
                                                    className={`form-control ${(formik.touched as any).email && (formik.errors as any).email ? "is-invalid" : ""}`}
                                                    style={{
                                                        width: "100%",
                                                        height: 39,
                                                        borderRadius: 10,
                                                        padding: "10px 12px",
                                                        borderWidth: 2,
                                                        borderStyle: "solid",
                                                        borderColor: (formik.touched as any).email && (formik.errors as any).email ? "#747981" : isEdit ? "#d1d5db" : "#c9ced6",
                                                        backgroundColor: isEdit ? "#f2f2f2" : "#f5f6fa",
                                                        outline: "none",
                                                        boxShadow: "none",
                                                        color: "#111827",
                                                        cursor: isEdit ? "not-allowed" : "text",
                                                        opacity: 1,
                                                    }}
                                                    value={formik.values.email}
                                                    onChange={(e) => formik.setFieldValue("email", e.target.value)}
                                                    onBlur={() => {
                                                        formik.setFieldValue("email", String(formik.values.email || "").trim());
                                                        formik.setFieldTouched("email", true);
                                                    }}
                                                    placeholder=""
                                                    disabled={isEdit}
                                                    readOnly={isEdit}
                                                    autoComplete="new-password"
                                                    autoCorrect="off"
                                                    autoCapitalize="off"
                                                    spellCheck={false}
                                                    data-lpignore="true"
                                                    data-1p-ignore="true"
                                                    data-bwignore="true"
                                                    data-form-type="other"
                                                />
                                                {(formik.touched as any).email && (formik.errors as any).email && (
                                                    <div className="invalid-feedback d-block">{String((formik.errors as any).email)}</div>
                                                )}
                                            </div>
                                            <div className="col-12">
                                                <label className="form-label">
                                                    Password <span className="text-danger">*</span>
                                                </label>
                                                <div className="position-relative">
                                                    <input
                                                        type={showPassword ? "text" : "password"}
                                                        className={`form-control br-8 pe-5 ${formik.touched.password && formik.errors.password ? " is-invalid" : ""}`}
                                                        id="password"
                                                        name="password"
                                                        value={formik.values.password}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        placeholder=""
                                                        disabled={isEdit}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                        onClick={() => setShowPassword(!showPassword)}
                                                        style={{ border: "none", background: "none", padding: "8px" }}
                                                    >
                                                        <i className={showPassword ? "ri-eye-off-line fs-17" : "ri-eye-line fs-17"} />
                                                    </button>
                                                </div>
                                                {formik.touched.password && formik.errors.password && (
                                                    <div className="text-danger mt-1">{formik.errors.password as any}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Profile / Assignment */}
                            <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white mt-4">
                                <div className="d-flex align-items-center mb-5">
                                    <i className="bi bi-image text-primary me-2" />
                                    <div className="fw-semibold">Profile</div>
                                </div>

                                <div className="row g-4 align-items-end">
                                    {/* Upload tile */}
                                    <div className="col-md-2">
                                        <div className="form-label">Profile Image</div>
                                        <label
                                            htmlFor="image"
                                            className="d-inline-flex align-items-center justify-content-center mb-2"
                                            style={{
                                                width: 120,
                                                height: 120,
                                                border: "2px dashed #d0d5dd",
                                                borderRadius: 12,
                                                cursor: "pointer",
                                            }}
                                            title="Upload image"
                                        >
                                            {preview || formik.values.profilePic ? (
                                                <img
                                                    src={preview || `${process.env.REACT_APP_BASE_URL}${formik.values.profilePic}`}
                                                    alt="Preview"
                                                    style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 10 }}
                                                />
                                            ) : (
                                                <i className="bi bi-upload" />
                                            )}
                                        </label>
                                        <input
                                            id="image"
                                            name="image"
                                            type="file"
                                            accept="image/*"
                                            className="d-none"
                                            onChange={(e) => {
                                                const f = e.currentTarget.files?.[0] ?? null;
                                                formik.setFieldValue("image", f);
                                            }}
                                            onBlur={formik.handleBlur}
                                        />
                                        <div>
                                            <button
                                                type="button"
                                                className="btn upload-btn mt-2"
                                                onClick={() => document.getElementById("image")?.click()}
                                            >
                                                Upload Image
                                            </button>
                                            <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                                                JPG, PNG up to 10MB
                                            </div>
                                        </div>
                                    </div>

                                    {/* Role */}
                                    <div className="col-md-2">
                                        <label className="form-label">
                                            Job Title / Role <span className="text-danger">*</span>
                                        </label>
                                        <AsyncPaginate
                                            classNamePrefix="rs"
                                            placeholder="Select job title/role"
                                            value={
                                                formik.values.jobTitle
                                                    ? {
                                                        value: (formik.values as any).jobTitleId || formik.values.jobTitle,
                                                        label:
                                                            formik.values.jobTitle === JOB_TITLE_CUSTOM
                                                                ? "Custom"
                                                                : formik.values.jobTitle,
                                                    }
                                                    : null
                                            }
                                            loadOptions={loadJobTitleOptions}
                                            onChange={(selected) => {
                                                formik.setFieldValue("jobTitle", selected?.value || "");
                                                formik.setFieldValue("jobTitleId", selected?.id || selected?.value || "");
                                            }}
                                            onBlur={() => formik.setFieldTouched("jobTitle", true)}
                                            additional={{ page: 1 }}
                                            defaultOptions={false}
                                            loadOptionsOnMenuOpen
                                            debounceTimeout={300}
                                            isClearable
                                            components={{ DropdownIndicator }}
                                            styles={customSelectStyles}
                                        />
                                        {formik.touched.jobTitle && formik.errors.jobTitle && (
                                            <div className="text-danger mt-1">{formik.errors.jobTitle as any}</div>
                                        )}
                                    </div>
                                    {formik.values.jobTitle === JOB_TITLE_CUSTOM ? (
                                        <div className="col-md-2">
                                            <UIInput
                                                formik={formik as any}
                                                name="customRole"
                                                label="Custom Role"
                                                required
                                                placeholder="Enter the custom role"
                                                {...noAutoSuggestFieldProps}
                                            />
                                        </div>
                                    ) : null}

                                    {/* Assign Offices (multi) */}
                                    <div className="col-md-3">
                                        <UIMultiSelectTeammber
                                            formik={formik as any}
                                            name="assignedOffices"
                                            label="Assign Offices"
                                            required
                                            options={officeOptions}
                                            placeholder="Select offices"
                                            isMulti
                                        />
                                    </div>

                                    {/* Role & Permissions */}
                                    <div className="col-md-3 mb-3">
                                        <label className="form-label">
                                            Role & Permissions <span className="text-danger">*</span>
                                        </label>
                                        <Select
                                            options={roleOptions}
                                            value={roleOptions.find(o => o.value === formik.values.brokeragePermissions) || null}
                                            onChange={(opt) => formik.setFieldValue("brokeragePermissions", opt?.value || "")}
                                            onBlur={() => formik.setFieldTouched("brokeragePermissions", true)}
                                            placeholder="Choose role/permission"
                                            classNamePrefix="rs"
                                            styles={customSelectStyles}
                                            components={{ DropdownIndicator }}
                                        />
                                        {formik.touched.brokeragePermissions && formik.errors.brokeragePermissions && (
                                            <div className="text-danger mt-1">{formik.errors.brokeragePermissions as any}</div>
                                        )}
                                    </div>

                                    {/* Status */}
                                    <div className="col-md-2">
                                        <UIReactSelect
                                            formik={formik as any}
                                            name="statuss"
                                            label="Status"
                                            // required
                                            options={STATUS_OPTIONS}
                                            placeholder="Select status"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* --- local styles (keep in same file) --- */}
                            <style>{`
  /* switcher: desktop table vs mobile cards */
  .perm-desktop { display: none; }
  .perm-mobile  { display: block; }
  @media (min-width: 768px) {
    .perm-desktop { display: block; }
    .perm-mobile  { display: none; }
  }

  /* mobile card grid */
  .perm-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px 10px;
    align-items: center;
  }
  @media (max-width: 380px) {
    .perm-grid { grid-template-columns: repeat(2, 1fr); }
  }
  .perm-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-direction: column;
    text-align: center;
  }
  .perm-checkbox { width: 20px; height: 20px; } /* larger tap target */
`}</style>

                            {/* Permissions & Assignment (accordion) */}
                            {/* <div className="shadow-border-1 box-shadow-datatables view-cards-shadow p-4 bg-white mt-4">
                                <div className="d-flex align-items-center mb-5">
                                    <i className="bi bi-sliders me-2 text-purple" />
                                    <div className="fw-semibold">Permissions & Assignment</div>
                                </div>


                                <div className="row g-3 mb-3">
                                    <div className="col-md-4">
                                        <UIMultiSelectTeammber
                                            formik={formik as any}
                                            name="permissions"
                                            label="Permissions Level"
                                            required
                                            options={PERMISSION_LEVELS}
                                            placeholder="Select permissions level"
                                            isMulti
                                            onChangeOverride={(opts: any) => {
                                                const vals: string[] = (opts || []).map((o: any) => o.value);
                                                formik.setFieldValue(
                                                    "permissions",
                                                    vals.includes("all") ? PERMISSION_LEVELS.map(o => o.value) : vals
                                                );
                                            }}
                                        />
                                    </div>
                                </div>


                                <div className="accordion" id="permAccordion">
                                    {PERM_GROUPS.map((g, idx) => (
                                        <div className="accordion-item mb-2 border rounded-3" key={g.key}>
                                            <h2 className="accordion-header">
                                                <button
                                                    className={`accordion-button ${idx !== 0 ? "collapsed" : ""}`}
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target={`#collapse-${g.key}`}
                                                    aria-expanded={idx === 0 ? "true" : "false"}
                                                >
                                                    {g.title}
                                                </button>
                                            </h2>

                                            <div
                                                id={`collapse-${g.key}`}
                                                className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                                                data-bs-parent="#permAccordion"
                                            >
                                                <div className="accordion-body p-0">


                                                    <div className="perm-mobile px-2">
                                                        {g.rows.map((r) => (
                                                            <div key={r} className="border rounded-3 bg-white p-3 mb-2 shadow-sm">
                                                                <div className="fw-semibold mb-2">{r}</div>

                                                                {g.key === "suppliers" ? (

                                                                    <div className="perm-grid">
                                                                        {SUPPLIER_KEYS.map((k) => {
                                                                            const inputId = `perm-${g.key}-${r}-${k}`;
                                                                            return (
                                                                                <label key={k} htmlFor={inputId} className="perm-cell">
                                                                                    <span className="small text-muted">{SUPPLIER_LABELS[k]}</span>
                                                                                    <input id={inputId} type="checkbox" className="form-check-input perm-checkbox" />
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (

                                                                    <div className="perm-grid">
                                                                        {CRUD_KEYS.map((k) => {
                                                                            const inputId = `perm-${g.key}-${r}-${k}`;
                                                                            const checked = !!permState[r]?.[k]; // k is CrudKey ✅
                                                                            return (
                                                                                <label key={k} htmlFor={inputId} className="perm-cell">
                                                                                    <span className="small text-muted">{CRUD_LABELS[k]}</span>
                                                                                    <input
                                                                                        id={inputId}
                                                                                        type="checkbox"
                                                                                        className="form-check-input perm-checkbox"
                                                                                        checked={checked}
                                                                                        onChange={() => toggleCell(r, k)}
                                                                                    />
                                                                                </label>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>


                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div> */}


                            {/* Footer */}
                            <div className="d-flex justify-content-end align-items-center mt-5">
                                <button type="button" className="btn btn-primary save-btn surface-btn fw-bold fs-16 me-3" disabled={formSubmitting} onClick={() => formik.handleSubmit()}>
                                    {formSubmitting ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save" : "Create Team Member"}
                                </button>
                                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" onClick={() => navigate(-1)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            {memberAccessPrompt ? (
                <div
                    className="modal fade show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.45)", zIndex: 2000 }}
                    onClick={closeMemberAccessPrompt}
                >
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content border-0 shadow roboto-new" style={{ borderRadius: 16 }}>
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title fs-19 mb-0">Existing team member</h5>
                                <button type="button" className="btn-close" disabled={formSubmitting} onClick={closeMemberAccessPrompt} />
                            </div>
                            <div className="modal-body pt-3">
                                <p className="mb-3" style={{ color: "#4b5563", fontWeight: 400, fontSize: 15 }}>{memberAccessPrompt.message}</p>
                                {memberAccessPrompt.name || memberAccessPrompt.email ? (
                                    <div className="mb-3">
                                        <label className="form-label mb-2">User Name</label>
                                        <div style={{ border: "1px solid #e5e7eb", background: "#fff", borderRadius: 12, padding: "12px 14px" }}>
                                            {memberAccessPrompt.name ? <div style={{ fontSize: 15, fontWeight: 500, color: "#101828" }}>{memberAccessPrompt.name}</div> : null}
                                            {memberAccessPrompt.email ? <div style={{ fontSize: 14, fontWeight: 400, color: "#667085" }}>{memberAccessPrompt.email}</div> : null}
                                        </div>
                                    </div>
                                ) : null}
                                <button
                                    type="button"
                                    className="w-100 text-start"
                                    onClick={() => setMemberAccessConfirmed(true)}
                                    style={{
                                        border: memberAccessConfirmed ? "1px solid #155dfc" : "1px solid #e5e7eb",
                                        background: memberAccessConfirmed ? "#eff6ff" : "#fff",
                                        color: memberAccessConfirmed ? "#155dfc" : "#101828",
                                        borderRadius: 12,
                                        padding: "12px 14px",
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 15,
                                        fontWeight: 500,
                                    }}
                                >
                                    <span
                                        className="d-inline-flex align-items-center justify-content-center me-2"
                                        style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            border: `2px solid ${memberAccessConfirmed ? "#155dfc" : "#d0d5dd"}`,
                                            verticalAlign: "middle",
                                        }}
                                    >
                                        {memberAccessConfirmed ? <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#155dfc" }} /> : null}
                                    </span>
                                    Ask the brokerage to give access to this team member
                                </button>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button type="button" className="btn back-btn surface-btn fw-bold fs-16" disabled={formSubmitting} onClick={closeMemberAccessPrompt}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary save-btn surface-btn fw-bold fs-16" disabled={formSubmitting} onClick={confirmExistingMemberAccess}>
                                    {formSubmitting ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
